import React from "react";
import "./Skeleton.css";
import Logo from "../assets/images/JMILOGO.svg";

/* --------------------------------------------------
   Single skeleton block — width/height/style via props
   -------------------------------------------------- */
export const SkeletonBlock = ({ className = "", style = {} }) => (
  <span className={`skeleton ${className}`} style={style} aria-hidden="true" />
);

/* --------------------------------------------------
   Catalog full-page skeleton (initial load)
   Matches the catalog-grid layout with card shapes
   -------------------------------------------------- */
export const CatalogSkeleton = ({ count = 6 }) => (
  <div className="catalog-skeleton" aria-label="Loading products…">
    <div className="catalog-skeleton-grid">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="skeleton-card">
          <SkeletonBlock className="skeleton-card-image" />
          <div className="skeleton-card-body">
            <SkeletonBlock className="skeleton-card-title" />
            <SkeletonBlock className="skeleton-card-sub" />
            <SkeletonBlock className="skeleton-card-weight" />
            <SkeletonBlock className="skeleton-card-btn" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* --------------------------------------------------
   Load-more row skeleton — shown at bottom of catalog
   while fetching the next page
   -------------------------------------------------- */
export const CatalogLoadMoreSkeleton = ({ count = 2 }) => (
  <div className="skeleton-load-more" aria-label="Loading more products…">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="skeleton-card">
        <SkeletonBlock className="skeleton-card-image" />
        <div className="skeleton-card-body">
          <SkeletonBlock className="skeleton-card-title" />
          <SkeletonBlock className="skeleton-card-sub" />
          <SkeletonBlock className="skeleton-card-btn" />
        </div>
      </div>
    ))}
  </div>
);

/* --------------------------------------------------
   Drawer skeleton — shown while spec is being fetched
   Sits inside the bottom-drawer shell
   -------------------------------------------------- */
export const DrawerSkeleton = () => (
  <div className="drawer-skeleton" aria-label="Loading product specifications…">
    <div className="drawer-skeleton-header">
      <SkeletonBlock className="drawer-skeleton-image" />
      <div className="drawer-skeleton-info">
        <SkeletonBlock className="drawer-skeleton-title" />
        <SkeletonBlock className="drawer-skeleton-line" />
        <SkeletonBlock className="drawer-skeleton-moq" />
      </div>
    </div>
    <div className="drawer-skeleton-row">
      <SkeletonBlock className="drawer-skeleton-select" />
      <SkeletonBlock className="drawer-skeleton-qty" />
    </div>
    <div className="drawer-skeleton-row">
      <SkeletonBlock className="drawer-skeleton-select" />
      <SkeletonBlock className="drawer-skeleton-qty" />
    </div>
    <SkeletonBlock className="drawer-skeleton-footer" />
  </div>
);

/* --------------------------------------------------
   ProductDetails skeleton — shown while spec loads
   -------------------------------------------------- */
export const ProductDetailsSkeleton = () => (
  <div
    className="product-details-skeleton"
    aria-label="Loading product details…"
  >
    <SkeletonBlock className="skeleton-detail-image" />
    <div className="skeleton-detail-body">
      <SkeletonBlock className="skeleton-detail-title" />
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="skeleton-spec-row">
          <SkeletonBlock className="skeleton-spec-label" />
          <SkeletonBlock className="skeleton-spec-value" />
        </div>
      ))}
      <SkeletonBlock className="skeleton-detail-btn" />
    </div>
  </div>
);

/* --------------------------------------------------
   Splash / auth skeleton — full-screen loading
   -------------------------------------------------- */
export const SplashSkeleton = () => (
  <div className="splash-skeleton" aria-label="Loading…">
    <img src={Logo} alt="JMI Logo" />
    {/* <SkeletonBlock className="splash-skeleton-logo" /> */}
    <SkeletonBlock className="splash-skeleton-title" />
    <SkeletonBlock className="splash-skeleton-sub" />
  </div>
);

/* --------------------------------------------------
   Orders list skeleton
   -------------------------------------------------- */
export const OrderListSkeleton = ({ count = 3 }) => (
  <div className="list-skeleton" aria-label="Loading orders…">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="order-skeleton-card">
        <div className="order-skeleton-header">
          <SkeletonBlock className="order-skeleton-title" />
          <SkeletonBlock className="order-skeleton-badge" />
        </div>
        <div className="order-skeleton-body">
          <SkeletonBlock className="order-skeleton-thumb" />
          <div className="order-skeleton-details">
            <SkeletonBlock className="order-skeleton-line" />
            <SkeletonBlock className="order-skeleton-line-sm" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

/* --------------------------------------------------
   Selections / Cart list skeleton
   -------------------------------------------------- */
export const SelectionsSkeleton = ({ count = 3 }) => (
  <div className="list-skeleton" aria-label="Loading selections…">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="order-skeleton-card">
        <div className="order-skeleton-body">
          <SkeletonBlock className="order-skeleton-thumb" />
          <div className="order-skeleton-details">
            <SkeletonBlock className="order-skeleton-title" />
            <SkeletonBlock className="order-skeleton-line" />
            <SkeletonBlock className="order-skeleton-line-sm" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

/* --------------------------------------------------
   Profile skeleton
   -------------------------------------------------- */
export const ProfileSkeleton = () => (
  <div className="profile-skeleton" aria-label="Loading profile…">
    <SkeletonBlock className="profile-skeleton-avatar" />
    <SkeletonBlock className="profile-skeleton-name" />
    <div className="profile-skeleton-card">
      <SkeletonBlock className="profile-skeleton-field" />
      <SkeletonBlock className="profile-skeleton-field" />
      <SkeletonBlock className="profile-skeleton-field" />
      <SkeletonBlock className="profile-skeleton-field" />
    </div>
  </div>
);

/* --------------------------------------------------
   Table / Live Rates skeleton
   -------------------------------------------------- */
export const TableSkeleton = ({ count = 4 }) => (
  <div className="table-skeleton" aria-label="Loading rates…">
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonBlock key={i} className="table-skeleton-row" />
    ))}
  </div>
);
